import { createAsyncThunk, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import { RootState, StoreState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import MessagingAPIService, { MessageQuery } from "messaging/_api/messaging-api-service";
import Forum from "messaging/forums/forum";
import Message from "messaging/message";
import Session from "users/__session/session";

const initialState: StoreState<PaginatedResponse<Message>> = {
  entries: {},
  loading: {},
  error: {},
};

export const populateForumMessages = createAsyncThunk(
  "messages/getMessagesByForum",
  async (
    {
      session,
      forum,
      query,
    }: {
      session: Session;
      forum: Forum;
      query: MessageQuery;
      append: boolean;
    },
    thunkAPI
  ) => {
    const apiService = new MessagingAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await apiService.getMessagesByForum(forum, query, abortController);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);

const messagesSlice = createSlice({
  name: "messages",
  initialState,
  reducers: {
    addMessage: (state, action: PayloadAction<Message>) => {
      const forumId = action.payload.forum.id?.value;
      if (!forumId) return;

      let existingForumMessages = state.entries[forumId];

      if (!existingForumMessages) {
        existingForumMessages = PaginatedResponse.empty<Message>();
      }

      existingForumMessages = existingForumMessages.appendOrReplaceData([action.payload]);
      state.entries[forumId] = existingForumMessages;
    },
    removeMessage: (state, action: PayloadAction<Guid>) => {
      for (const [forumId, forumMessages] of Object.entries(state.entries)) {
        if (!forumMessages) continue;
        state.entries[forumId] = forumMessages.removeDataById(action.payload);
      }
    },
    clearMessages: (state, action: PayloadAction<Guid>) => {
      const forumId = action.payload.value;
      if (!forumId) return;

      state.entries[forumId] = PaginatedResponse.empty<Message>();
      state.loading[forumId] = false;
      state.error[forumId] = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(populateForumMessages.pending, (state, action) => {
      const forumId = action.meta.arg.forum.id?.value;
      if (!forumId) return;

      state.loading[forumId] = true;
      state.error[forumId] = null;
    });
    builder.addCase(populateForumMessages.fulfilled, (state, action) => {
      const forumId = action.meta.arg.forum.id?.value;
      if (!forumId) return;

      let existingForumMessages = state.entries[forumId] ?? PaginatedResponse.empty();
      if (!existingForumMessages.isEmpty && action.meta.arg.append) {
        existingForumMessages = existingForumMessages.appendOrReplaceData(
          action.payload.data,
          action.payload.headers,
          (a, b) => (a.created?.diff(b.created) ?? 0) || (a.published?.diff(b.published) ?? 0)
        );
        state.entries[forumId] = existingForumMessages;
      } else {
        state.entries[forumId] = action.payload;
      }
      state.loading[forumId] = false;
    });
    builder.addCase(populateForumMessages.rejected, (state, action) => {
      const forumId = action.meta.arg.forum.id?.value;
      if (!forumId) return;

      state.loading[forumId] = false;
      state.error[forumId] = action.error;
    });
  },
});

const selectEntries = (state: RootState) => state.messages.entries;

const selectMessagesByForumId = createSelector(
  [selectEntries, (_: RootState, forumId?: Guid) => forumId?.value ?? ""],
  (entries, id) => entries[id] ?? []
);

export const { addMessage, removeMessage, clearMessages } = messagesSlice.actions;

export const getIsLoadingMessagesByForumId = (forumId?: Guid) => (state: RootState) => {
  if (!forumId) return false;
  return state.messages?.loading[forumId.value];
};
export const getMessagesByForumId = (forumId?: Guid) => (state: RootState) => selectMessagesByForumId(state, forumId);

export const getErrorLoadingMessagesByForumId = (forumId?: Guid) => (state: RootState) =>
  state.messages.error[forumId?.value ?? ""];

export const getMessageHasImagesAttached = (message: Message) => {
  return message.attachments.some((attachment) => attachment.isImage);
};

export default messagesSlice;
